<template>
  <div>
    <div class="input-group" style="position: absolute; right: 10px;">
      <div class="input-group-prepend">
        <label class="input-group-text">Language</label>
      </div>
      <select class="custom-select" v-model="language" v-on:change="changeLanguage">
        <option value="en">English</option>
        <option value="ko">한국어</option>
      </select>
    </div>

    <table>
      <tr>
        <td>
          <h1>{{ msg }}</h1>
          <ul class="instruction" ref="instruction">
            <li>1. Install Chrome browser.</li>
            <li>2. Copy and paste the code in the browser search bar.</li>
            <li>
              <code>
                chrome://flags/#enable-experimental-web-platform-features
              </code>
            </li>
            <li>3. Enable [Experimental Web Platform features].</li>
            <li>4. Restart the browser.</li>
          </ul>
        </td>
      </tr>
      <tr>
        <td>
          <div class="btn-group" role="group">
            <button type="button" class="btn btn-warning" v-on:click="connectDevice" ref="connectButton"><box-icon name="chip" color="white"></box-icon></button>
            <button type="button" class="btn btn-primary" v-on:click="runCode" ref="runButton" disabled="true">
              <box-icon name="play" color="white"></box-icon>
            </button>
            <button type="button" class="btn btn-danger" v-on:click="stopCode" ref="stopButton">
              <box-icon name="stop" color="white"></box-icon>
            </button>
          </div>
        </td>
      </tr>
      <tr>
        <td ref="blocklyArea" id="blocklyArea"></td>
      </tr>
    </table>
  
    <div ref="blocklyDiv" style="position: absolute"></div>

    <xml ref="toolbox" style="display: none">
      <category name="DynamiKontrol" colour="0">
        <block type="motor_angle">
          <value name="VAR">
            <shadow type="math_number">
              <field name="NUM">45</field>
            </shadow>
          </value>
        </block>
        <block type="status_led"></block>
        <block type="wait_seconds"></block>
      </category>
      <sep></sep>
      <category name="Logic" categorystyle="logic_category">
        <block type="controls_if"></block>
        <block type="logic_compare"></block>
        <block type="logic_operation"></block>
        <block type="logic_negate"></block>
        <block type="logic_boolean"></block>
        <block type="logic_null" disabled="true"></block>
        <block type="logic_ternary"></block>
      </category>
      <category name="Loops" categorystyle="loop_category">
        <block type="controls_repeat_ext">
          <value name="TIMES">
            <shadow type="math_number">
              <field name="NUM">10</field>
            </shadow>
          </value>
        </block>
        <block type="controls_repeat" disabled="true"></block>
        <block type="controls_whileUntil"></block>
        <block type="controls_for">
          <value name="FROM">
            <shadow type="math_number">
              <field name="NUM">1</field>
            </shadow>
          </value>
          <value name="TO">
            <shadow type="math_number">
              <field name="NUM">10</field>
            </shadow>
          </value>
          <value name="BY">
            <shadow type="math_number">
              <field name="NUM">1</field>
            </shadow>
          </value>
        </block>
        <block type="controls_forEach"></block>
        <block type="controls_flow_statements"></block>
      </category>
      <category name="Math" categorystyle="math_category">
        <block type="math_number" gap="32">
          <field name="NUM">123</field>
        </block>
        <block type="math_arithmetic">
          <value name="A">
            <shadow type="math_number">
              <field name="NUM">1</field>
            </shadow>
          </value>
          <value name="B">
            <shadow type="math_number">
              <field name="NUM">1</field>
            </shadow>
          </value>
        </block>
        <block type="math_single">
          <value name="NUM">
            <shadow type="math_number">
              <field name="NUM">9</field>
            </shadow>
          </value>
        </block>
        <block type="math_trig">
          <value name="NUM">
            <shadow type="math_number">
              <field name="NUM">45</field>
            </shadow>
          </value>
        </block>
        <block type="math_constant"></block>
        <block type="math_number_property">
          <value name="NUMBER_TO_CHECK">
            <shadow type="math_number">
              <field name="NUM">0</field>
            </shadow>
          </value>
        </block>
        <block type="math_round">
          <value name="NUM">
            <shadow type="math_number">
              <field name="NUM">3.1</field>
            </shadow>
          </value>
        </block>
        <block type="math_on_list"></block>
        <block type="math_modulo">
          <value name="DIVIDEND">
            <shadow type="math_number">
              <field name="NUM">64</field>
            </shadow>
          </value>
          <value name="DIVISOR">
            <shadow type="math_number">
              <field name="NUM">10</field>
            </shadow>
          </value>
        </block>
        <block type="math_constrain">
          <value name="VALUE">
            <shadow type="math_number">
              <field name="NUM">50</field>
            </shadow>
          </value>
          <value name="LOW">
            <shadow type="math_number">
              <field name="NUM">1</field>
            </shadow>
          </value>
          <value name="HIGH">
            <shadow type="math_number">
              <field name="NUM">100</field>
            </shadow>
          </value>
        </block>
        <block type="math_random_int">
          <value name="FROM">
            <shadow type="math_number">
              <field name="NUM">1</field>
            </shadow>
          </value>
          <value name="TO">
            <shadow type="math_number">
              <field name="NUM">100</field>
            </shadow>
          </value>
        </block>
        <block type="math_random_float"></block>
        <block type="math_atan2">
          <value name="X">
            <shadow type="math_number">
              <field name="NUM">1</field>
            </shadow>
          </value>
          <value name="Y">
            <shadow type="math_number">
              <field name="NUM">1</field>
            </shadow>
          </value>
        </block>
      </category>
      <category name="Text" categorystyle="text_category">
        <block type="text"></block>
        <block type="text_multiline"></block>
        <block type="text_join"></block>
        <block type="text_append">
          <value name="TEXT">
            <shadow type="text"></shadow>
          </value>
        </block>
        <block type="text_length">
          <value name="VALUE">
            <shadow type="text">
              <field name="TEXT">abc</field>
            </shadow>
          </value>
        </block>
        <block type="text_isEmpty">
          <value name="VALUE">
            <shadow type="text">
              <field name="TEXT"></field>
            </shadow>
          </value>
        </block>
        <block type="text_indexOf">
          <value name="VALUE">
            <block type="variables_get">
              <field name="VAR">text</field>
            </block>
          </value>
          <value name="FIND">
            <shadow type="text">
              <field name="TEXT">abc</field>
            </shadow>
          </value>
        </block>
        <block type="text_charAt">
          <value name="VALUE">
            <block type="variables_get">
              <field name="VAR">text</field>
            </block>
          </value>
        </block>
        <block type="text_getSubstring">
          <value name="STRING">
            <block type="variables_get">
              <field name="VAR">text</field>
            </block>
          </value>
        </block>
        <block type="text_changeCase">
          <value name="TEXT">
            <shadow type="text">
              <field name="TEXT">abc</field>
            </shadow>
          </value>
        </block>
        <block type="text_trim">
          <value name="TEXT">
            <shadow type="text">
              <field name="TEXT">abc</field>
            </shadow>
          </value>
        </block>
        <block type="text_count">
          <value name="SUB">
            <shadow type="text"></shadow>
          </value>
          <value name="TEXT">
            <shadow type="text"></shadow>
          </value>
        </block>
        <block type="text_replace">
          <value name="FROM">
            <shadow type="text"></shadow>
          </value>
          <value name="TO">
            <shadow type="text"></shadow>
          </value>
          <value name="TEXT">
            <shadow type="text"></shadow>
          </value>
        </block>
        <block type="text_reverse">
          <value name="TEXT">
            <shadow type="text"></shadow>
          </value>
        </block>
        <label text="Input/Output:" web-class="ioLabel"></label>
        <block type="text_print">
          <value name="TEXT">
            <shadow type="text">
              <field name="TEXT">abc</field>
            </shadow>
          </value>
        </block>
        <block type="text_prompt_ext">
          <value name="TEXT">
            <shadow type="text">
              <field name="TEXT">abc</field>
            </shadow>
          </value>
        </block>
      </category>
      <sep></sep>
      <category name="Variables" categorystyle="variable_category" custom="VARIABLE"></category>
      <category name="Functions" categorystyle="procedure_category" custom="PROCEDURE"></category>
    </xml>

    <textarea ref="codeArea" style="width:500px; display: none;" rows="10"></textarea>
  </div>
</template>

<script>
import Blockly from 'blockly'
import * as Ko from 'blockly/msg/ko'
import * as En from 'blockly/msg/en'
import Vue from 'vue'
import SerialPort from '@serialport/stream'
import WSABinding from 'serialport-binding-webserialapi'
import Interpreter from 'js-interpreter'
import 'boxicons'

Vue.config.ignoredElements = [
  'xml',
  'block',
  'category',
  'sep',
  'value',
  'mutation',
  'field',
  'shadow'
]

export default {
  name: 'Main',
  props: {
    msg: String
  },
  data() {
    return {
      isConnected: false,
      blocklyDiv: null,
      workspace: null,
      codeArea: null,
      runButton: null,
      port: '',
      encoder: new TextEncoder(),
      decoder: new TextDecoder(),
      myInterpreter: null,
      runner: null,
      highlightPause: false,
      latestCode: '',
      languages: {
        'en': En,
        'ko': Ko
      },
      language: 'ko'
    }
  },
  methods: {
    changeLanguage(reload=true) {
      Blockly.setLocale(this.languages[this.language])

      const instructions = {
        en: `<li>1. Install Chrome browser.</li>
            <li>2. Copy and paste the code in the browser search bar.</li>
            <li>
              <code>
                chrome://flags/#enable-experimental-web-platform-features
              </code>
            </li>
            <li>3. Enable [Experimental Web Platform features].</li>
            <li>4. Restart the browser.</li>
            <li>5. Click the yellow chip button and connect The Matrix DynamiKontrol to serial port</li>
            <li>6. Enjoy coding!</li>`,
        ko: `<li>1. 크롬 브라우저를 설치해주세요.</li>
            <li>2. 주소표시줄에 아래 코드를 입력하고 엔터를 눌러주세요.</li>
            <li>
              <code>
                chrome://flags/#enable-experimental-web-platform-features
              </code>
            </li>
            <li>3. [Experimental Web Platform features] 옵션을 Enabled로 변경해주세요.</li>
            <li>4. 브라우저를 종료하고 다시 시작해주세요.</li>
            <li>5. 노란색 칩 버튼을 클릭하고, The Matrix DynamiKontrol을 시리얼 포트로 연결해주세요.</li>
            <li>6. Enjoy coding!</li>`
      }

      this.$refs.instruction.innerHTML = instructions[this.language]

      if (reload) {
        const urlParams = new URLSearchParams(window.location.search)
        urlParams.set('lang', this.language)
        window.location.search = urlParams
      }
    },
    onresizeWorkspace() {
      let blocklyArea = this.$refs.blocklyArea

      let element = blocklyArea
      let x = 0
      let y = 0
      do {
        x += element.offsetLeft
        y += element.offsetTop
        element = element.offsetParent
      } while (element)

      this.blocklyDiv.style.left = x + 'px'
      this.blocklyDiv.style.top = y + 'px'
      this.blocklyDiv.style.width = blocklyArea.offsetWidth + 'px'
      this.blocklyDiv.style.height = blocklyArea.offsetHeight + 'px'
      Blockly.svgResize(this.workspace)
    },
    motorAngle(direction, angle) {
      const type = 0x03
      const command = 0x00

      const dataLength = 2
      direction = direction == 'CW' ? 0x00 : 0x01

      const checksum = type ^ command ^ dataLength ^ direction ^ angle

      this.send([0x05, type, command, dataLength, direction , angle, checksum, 0x04])
    },
    led(color, status) {
      const type = 0x01

      let command = 0x00
      switch(color) {
        case '#ff0000':
          command = 0x00
          break
        case '#ffff00':
          command = 0x01
          break
        case '#00ff00':
          command = 0x02
      }

      let mode = 0x00
      switch(status) {
        case 'OFF':
          mode = 0x00
          break
        case 'ON':
          mode = 0x01
          break
        case 'TOGGLE':
          mode = 0x02
          break
        case 'BLINK':
          mode = 0x03
          break
      }

      const on_low_bit = 0xff
      const on_high_bit = 0x00
      const off_low_bit = 0xff
      const off_high_bit = 0x00

      const dataLength = 5

      const checksum = type ^ command ^ dataLength ^ mode ^ on_high_bit ^ on_low_bit ^ off_high_bit ^ off_low_bit

      this.send([0x05, type, command, dataLength, mode , on_high_bit, on_low_bit, off_high_bit, off_low_bit, checksum, 0x04])
    },
    initApi(interpreter, globalObject) {
      let self = this

      // Add an API function for the alert() block, generated for "text_print" blocks.
      interpreter.setProperty(globalObject, 'alert',
        interpreter.createNativeFunction((text) => {
          text = text ? text.toString() : '';
          alert(text)
      }));

      // Add an API function for the prompt() block.
      interpreter.setProperty(globalObject, 'prompt',
        interpreter.createNativeFunction((text) => {
          text = text ? text.toString() : '';
          return interpreter.createPrimitive(prompt(text));
      }));

      interpreter.setProperty(globalObject, 'highlightBlock',
        interpreter.createNativeFunction((id) => {
          id = id ? id.toString() : '';
          return interpreter.createPrimitive(self.highlightBlock(id));
      }));

      this.initInterpreterWaitForSeconds(interpreter, globalObject);

      interpreter.setProperty(globalObject, 'motorAngle',
        interpreter.createNativeFunction((direction, angle) => {
          self.motorAngle(direction, angle)
      }))

      interpreter.setProperty(globalObject, 'led',
        interpreter.createNativeFunction((color, status) => {
          self.led(color, status)
      }))
    },
    highlightBlock(id) {
      this.workspace.highlightBlock(id);
      this.highlightPause = true;
    },
    resetStepUi() {
      this.workspace.highlightBlock(null);
      this.highlightPause = false;
    },
    initInterpreterWaitForSeconds(interpreter, globalObject) {
      // Ensure function name does not conflict with variable names.
      Blockly.JavaScript.addReservedWords('waitForSeconds');

      let wrapper = interpreter.createAsyncFunction((timeInSeconds, callback) => {
        // Delay the call to the callback.
        setTimeout(callback, timeInSeconds * 1000);
      });
      interpreter.setProperty(globalObject, 'waitForSeconds', wrapper);
    },
    generateCodeAndLoadIntoInterpreter() {
      // Generate JavaScript code and parse it.
      Blockly.JavaScript.STATEMENT_PREFIX = 'highlightBlock(%1);\n';
      Blockly.JavaScript.addReservedWords('highlightBlock');
      this.latestCode = Blockly.JavaScript.workspaceToCode(this.workspace);
      this.codeArea.value = this.latestCode
      this.resetStepUi();
    },
    runCode() {
      this.runButton.disabled = true

      if (!this.myInterpreter) {
        // First statement of this code.
        // Clear the program output.
        this.resetStepUi();

        // And then show generated code in an alert.
        // In a timeout to allow the outputArea.value to reset first.
        let self = this

        setTimeout(() => {
          // Begin execution
          self.highlightPause = false;
          self.myInterpreter = new Interpreter(self.latestCode, self.initApi);
          self.runner = () => {
            if (self.myInterpreter) {
              var hasMore = self.myInterpreter.run();
              if (hasMore) {
                // Execution is currently blocked by some async call.
                // Try again later.
                setTimeout(self.runner, 10);
              } else {
                // Program is complete.
                self.resetInterpreter();
                self.resetStepUi();
              }
            }
          };
          self.runner();
        }, 1);
        return;
      }
    },
    stopCode() {
      this.resetInterpreter();
      this.resetStepUi();
    },
    resetInterpreter() {
      if (this.isConnected) {
        this.runButton.disabled = false
      }
      this.myInterpreter = null;
      if (this.runner) {
        clearTimeout(this.runner);
        this.runner = null;
      }
    },
    send(data) {
      data = Uint8Array.from(data)

      console.log(data)

      this.port.write(data, err => {
        if (err) {
          console.log('Error on write: ', err.message)
        }
      })
    },
    receive(data) {
      // data = Buffer.from(data).toString('hex')
      console.log(data)
    },
    connectDevice() {
      WSABinding.portFilters = [{ usbVendorId: 0x3476 }]
      // WSABinding.debug = true
      SerialPort.Binding = WSABinding
      SerialPort.list().then(portsList => {
        for (let port of portsList) {
          // let pid = port.productId
          // let vid = port.vendorId
          // if (vid != 0x3476) continue

          this.port = new SerialPort(port.path, {
            baudRate: 115200,
            autoOpen: true
          })

          this.port.on('data', this.receive)

          this.send([0x05, 0x00, 0x00, 0x00, 0x00, 0x04])

          this.$refs.connectButton.disabled = true
          this.$refs.runButton.disabled = false
          this.isConnected = true
        }
      }, err => {
        console.error(err)
      })
    }
  },
  mounted() {
    // TODO support windows
    // TODO connection unavailable sometimes
    // this.$refs.connectButton.click()

    this.runButton = this.$refs.runButton

    // motor angle block
    En['MOTOR_ANGLE_TITLE'] = 'motor angle %1 in %2'
    Ko['MOTOR_ANGLE_TITLE'] = '모터를 %2으로 %1도 회전하기'
    En['MOTOR_ANGLE_CLOCKWISE'] = 'clockwise'
    Ko['MOTOR_ANGLE_CLOCKWISE'] = '시계방향'
    En['MOTOR_ANGLE_COUNTER_CLOCKWISE'] = 'counter clockwise'
    Ko['MOTOR_ANGLE_COUNTER_CLOCKWISE'] = '반시계방향'

    Blockly.defineBlocksWithJsonArray([{
      "type": "motor_angle",
      "message0": "%{BKY_MOTOR_ANGLE_TITLE}",
      "args0": [
        {"type": "input_value", "name": "VAR"},
        {"type": "field_dropdown", "name": "DIR", "options": [[ "%{BKY_MOTOR_ANGLE_CLOCKWISE}", "CW" ], [ "%{BKY_MOTOR_ANGLE_COUNTER_CLOCKWISE}", "CCW" ]]},
      ],
      "previousStatement": null,
      "nextStatement": null,
      "colour": 0
    }]);

    Blockly.JavaScript['motor_angle'] = (block) => {
      const direction = block.getFieldValue('DIR')
      const angle = Blockly.JavaScript.valueToCode(block, 'VAR', Blockly.JavaScript.ORDER_NONE)

      // let code = `window.vm.$children[0].$refs.main.motorAngle('${direction}', ${angle});`
      let code = `motorAngle('${direction}', ${angle});`
      return code
    }

    // status led block
    En['STATUS_LED_TITLE'] = 'turn %1 %2 status led'
    Ko['STATUS_LED_TITLE'] = '%2색 LED를 %1'
    En['STATUS_LED_STATUS_ON'] = 'on'
    Ko['STATUS_LED_STATUS_ON'] = '켜기'
    En['STATUS_LED_STATUS_OFF'] = 'off'
    Ko['STATUS_LED_STATUS_OFF'] = '끄기'
    En['STATUS_LED_STATUS_TOGGLE'] = 'toggle'
    Ko['STATUS_LED_STATUS_TOGGLE'] = '토글하기'
    En['STATUS_LED_STATUS_BLINK'] = 'blink'
    Ko['STATUS_LED_STATUS_BLINK'] = '깜빡이기'

    Blockly.defineBlocksWithJsonArray([{
      "type": "status_led",
      "message0": "%{BKY_STATUS_LED_TITLE}",
      "args0": [{
        "type": "field_dropdown",
        "name": "STATUS",
        "options": [
          [ "%{BKY_STATUS_LED_STATUS_ON}", "ON" ],
          [ "%{BKY_STATUS_LED_STATUS_OFF}", "OFF" ],
          [ "%{BKY_STATUS_LED_STATUS_TOGGLE}", "TOGGLE" ],
          [ "%{BKY_STATUS_LED_STATUS_BLINK}", "BLINK" ]
        ]
      }, {
        "type": "field_colour",
        "name": "COLOR",
        "colourOptions": ['#ff0000', '#ffff00', '#00ff00'],
        "colourTitles": ['red', 'yellow', 'green'],
        "colour": '#ff0000'
      }],
      "previousStatement": null,
      "nextStatement": null,
      "colour": "180"
    }]);

    Blockly.JavaScript['status_led'] = (block) => {
      const color = String(block.getFieldValue('COLOR'));
      const status = String(block.getFieldValue('STATUS'));
      return `led('${color}', '${status}');\n`;
    };

    // wait block
    En['WAIT_SECONDS_TITLE'] = 'wait %1 seconds'
    Ko['WAIT_SECONDS_TITLE'] = '%1초 기다리기'

    Blockly.defineBlocksWithJsonArray([{
      "type": "wait_seconds",
      "message0": "%{BKY_WAIT_SECONDS_TITLE}",
      "args0": [{
        "type": "field_number",
        "name": "SECONDS",
        "min": 0,
        "max": 600,
        "value": 1
      }],
      "previousStatement": null,
      "nextStatement": null,
      "colour": "%{BKY_LOOPS_HUE}"
    }]);

    Blockly.JavaScript['wait_seconds'] = (block) => {
      var seconds = Number(block.getFieldValue('SECONDS'));
      var code = 'waitForSeconds(' + seconds + ');\n';
      return code;
    };

    this.blocklyDiv = this.$refs.blocklyDiv
    this.codeArea = this.$refs.codeArea

    Blockly.Themes.Halloween = Blockly.Theme.defineTheme('halloween', {
      'base': Blockly.Themes.Classic,
      'componentStyles': {
        'workspaceBackgroundColour': '#eee',
        'toolboxBackgroundColour': '#999',
        'toolboxForegroundColour': '#fff',
        'flyoutBackgroundColour': '#252526',
        'flyoutForegroundColour': '#ccc',
        'flyoutOpacity': 1,
        // 'scrollbarColour': '#ff0000',
        'insertionMarkerColour': '#fff',
        'insertionMarkerOpacity': 0.3,
        'scrollbarOpacity': 0.4,
        'cursorColour': '#d0d0d0',
        'blackBackground': '#333'
      }
    });

    this.workspace = Blockly.inject(this.blocklyDiv, {
      toolbox: this.$refs.toolbox,
      theme: Blockly.Themes.Halloween,
      zoom: {
        controls: true,
        wheel: true,
        startScale: 1.4,
        maxScale: 3.0,
        minScale: 0.3,
        scaleSpeed: 1.2,
        pinch: true },
      trashcan: true
    })

    const lang = new URL(location.href).searchParams.get('lang')
    if (lang) {
      this.language = lang
    }
    this.changeLanguage(false)

    window.addEventListener('resize', this.onresizeWorkspace, false)
    this.onresizeWorkspace()

    Blockly.svgResize(this.workspace)

    // interpreter
    this.generateCodeAndLoadIntoInterpreter();

    this.workspace.addChangeListener(() => {
      this.resetInterpreter();
      this.generateCodeAndLoadIntoInterpreter();
    })
  }
}
</script>

<style scoped>
table {
  height: 100%;
  width: 100%;
}
#blocklyArea {
  height: 99%;
}
.instruction {
  list-style: none;
}
</style>
